import { useMemo } from "react";
import { ComplianceCaseTypeEnum as ComplianceCaseTypeEnumValues } from "~/graphql/supergraphEnums";
import { useGetProfileForComparisonQuery } from "~/graphql/supergraphHooks";
import { useIsFatcaCrsEnabledForCase } from "~/pages/Compliance/flags";
import { buildRows } from "./buildRows";
import type { ComparisonRow } from "./types";
import type { ComplianceCaseTypeEnum } from "~/graphql/supergraphTypes";
import type { ProfileData } from "~/pages/Compliance/ProposedChangesModal/types";

type QueryResult = ReturnType<typeof useGetProfileForComparisonQuery>;

export type UseComparisonRowsArgs = {
  sourceProfileId: number;
  targetProfileId: number;
  latestVersionId: number | null;
  caseId: number | undefined;
  programId: number | undefined;
  caseType: ComplianceCaseTypeEnum | undefined;
  fatcaCrsEnabled: boolean;
};

export type UseComparisonRowsResult = {
  queryResults: QueryResult[];
  rows: ComparisonRow[];
  currentProfile: ProfileData | undefined;
  publishedProfile: ProfileData | undefined;
  proposedProfile: ProfileData | undefined;
};

export const useComparisonRows = ({
  sourceProfileId,
  targetProfileId,
  latestVersionId,
  caseId,
  programId,
  caseType,
  fatcaCrsEnabled,
}: UseComparisonRowsArgs): UseComparisonRowsResult => {
  const showFatcaCrs = useIsFatcaCrsEnabledForCase(
    caseType as ComplianceCaseTypeEnumValues | undefined,
    fatcaCrsEnabled,
  );

  const currentQuery = useGetProfileForComparisonQuery({
    variables: { profileId: sourceProfileId, programId, caseId, showFatcaCrs },
    skip: !sourceProfileId,
    fetchPolicy: "no-cache",
  });

  const proposedQuery = useGetProfileForComparisonQuery({
    variables: { profileId: targetProfileId, programId, caseId, showFatcaCrs },
    skip: !targetProfileId,
    fetchPolicy: "no-cache",
  });

  const publishedQuery = useGetProfileForComparisonQuery({
    variables: { profileId: latestVersionId ?? 0, programId, caseId, showFatcaCrs },
    skip: latestVersionId == null,
    fetchPolicy: "no-cache",
  });

  const currentProfile = currentQuery.data?.complianceProfile ?? undefined;
  const proposedProfile = proposedQuery.data?.complianceProfile ?? undefined;
  const publishedProfile = publishedQuery.data?.complianceProfile ?? undefined;

  const rows = useMemo(
    () => buildRows({ currentProfile, publishedProfile, proposedProfile }),
    [currentProfile, publishedProfile, proposedProfile],
  );

  const queryResults = useMemo(() => {
    const results = [currentQuery, proposedQuery];
    if (latestVersionId != null) results.push(publishedQuery);
    return results;
  }, [currentQuery, proposedQuery, publishedQuery, latestVersionId]);

  return {
    queryResults,
    rows,
    currentProfile,
    publishedProfile,
    proposedProfile,
  };
};
